import { useState, useEffect, useCallback, useRef } from "react";

const API_URL = "https://script.google.com/macros/s/AKfycbz8qddFkaJBOlxFgaNEJ_rZzEGkX4eN8B_4N4UywTN6WgAks0tStH-ADiLdJQ2h6OJC7w/exec";

// ── Barcode Generator (Code 128) ──────────────────────────────
function BarcodeCanvas({value, width=2, height=60, fontSize=12}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !value) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Code 128 encoding
    const CODE128_START_B = 104;
    const CODE128_STOP = 106;
    const CODE128_PATTERNS = [
      "11011001100","11001101100","11001100110","10010011000","10010001100",
      "10001001100","10011001000","10011000100","10001100100","11001001000",
      "11001000100","11000100100","10110011100","10011011100","10011001110",
      "10111001100","10011101100","10011100110","11001110010","11001011100",
      "11001001110","11011100100","11001110100","11101101110","11101001100",
      "11100101100","11100100110","11101100100","11100110100","11100110010",
      "11011011000","11011000110","11000110110","10100011000","10001011000",
      "10001000110","10110001000","10001101000","10001100010","11010001000",
      "11000101000","11000100010","10110111000","10110001110","10001101110",
      "10111011000","10111000110","10001110110","11101110110","11010001110",
      "11000101110","11011101000","11011100010","11011101110","11101011000",
      "11101000110","11100010110","11101101000","11101100010","11100011010",
      "11101111010","11001000010","11110001010","10100110000","10100001100",
      "10010110000","10010000110","10000101100","10000100110","10110010000",
      "10110000100","10011010000","10011000010","10000110100","10000110010",
      "11000010010","11001010000","11110111010","11000010100","10001111010",
      "10100111100","10010111100","10010011110","10111100100","10011110100",
      "10011110010","11110100100","11110010100","11110010010","11011011110",
      "11011110110","11110110110","10101111000","10100011110","10001011110",
      "10111101000","10111100010","11110101000","11110100010","10111011110",
      "10111101110","11101011110","11110101110","11010000100","11010010000",
      "11010011100","1100011101011"
    ];

    const getCharCode = (c) => {
      const code = c.charCodeAt(0);
      if (code >= 32 && code <= 126) return code - 32;
      return -1;
    };

    // Build pattern
    let codes = [CODE128_START_B];
    let checksum = CODE128_START_B;
    for (let i = 0; i < value.length; i++) {
      const c = getCharCode(value[i]);
      if (c >= 0) {
        codes.push(c);
        checksum += c * (i + 1);
      }
    }
    codes.push(checksum % 103);
    codes.push(CODE128_STOP);

    let pattern = "";
    for (const code of codes) {
      if (CODE128_PATTERNS[code]) pattern += CODE128_PATTERNS[code];
    }

    const quietZone = 10;
    const totalWidth = pattern.length * width + quietZone * 2;
    const textHeight = fontSize > 0 ? fontSize + 4 : 0;
    const totalHeight = height + textHeight;

    canvas.width = totalWidth;
    canvas.height = totalHeight;

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    ctx.fillStyle = "#000";
    let x = quietZone;
    for (const bit of pattern) {
      if (bit === "1") ctx.fillRect(x, 0, width, height);
      x += width;
    }

    if (fontSize > 0) {
      ctx.fillStyle = "#000";
      ctx.font = `${fontSize}px monospace`;
      ctx.textAlign = "center";
      ctx.fillText(value, totalWidth / 2, height + fontSize);
    }
  }, [value, width, height, fontSize]);

  return <canvas ref={canvasRef} style={{display:"block"}}/>;
}

function getBarcodeDataURL(value, width=2, height=60) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const CODE128_START_B = 104;
  const CODE128_STOP = 106;
  const CODE128_PATTERNS = [
    "11011001100","11001101100","11001100110","10010011000","10010001100",
    "10001001100","10011001000","10011000100","10001100100","11001001000",
    "11001000100","11000100100","10110011100","10011011100","10011001110",
    "10111001100","10011101100","10011100110","11001110010","11001011100",
    "11001001110","11011100100","11001110100","11101101110","11101001100",
    "11100101100","11100100110","11101100100","11100110100","11100110010",
    "11011011000","11011000110","11000110110","10100011000","10001011000",
    "10001000110","10110001000","10001101000","10001100010","11010001000",
    "11000101000","11000100010","10110111000","10110001110","10001101110",
    "10111011000","10111000110","10001110110","11101110110","11010001110",
    "11000101110","11011101000","11011100010","11011101110","11101011000",
    "11101000110","11100010110","11101101000","11101100010","11100011010",
    "11101111010","11001000010","11110001010","10100110000","10100001100",
    "10010110000","10010000110","10000101100","10000100110","10110010000",
    "10110000100","10011010000","10011000010","10000110100","10000110010",
    "11000010010","11001010000","11110111010","11000010100","10001111010",
    "10100111100","10010111100","10010011110","10111100100","10011110100",
    "10011110010","11110100100","11110010100","11110010010","11011011110",
    "11011110110","11110110110","10101111000","10100011110","10001011110",
    "10111101000","10111100010","11110101000","11110100010","10111011110",
    "10111101110","11101011110","11110101110","11010000100","11010010000",
    "11010011100","1100011101011"
  ];

  const getCharCode = (c) => {
    const code = c.charCodeAt(0);
    return (code >= 32 && code <= 126) ? code - 32 : -1;
  };

  let codes = [CODE128_START_B];
  let checksum = CODE128_START_B;
  for (let i = 0; i < value.length; i++) {
    const c = getCharCode(value[i]);
    if (c >= 0) { codes.push(c); checksum += c * (i + 1); }
  }
  codes.push(checksum % 103);
  codes.push(CODE128_STOP);

  let pattern = "";
  for (const code of codes) {
    if (CODE128_PATTERNS[code]) pattern += CODE128_PATTERNS[code];
  }

  const quietZone = 10;
  const fontSize = 12;
  const totalWidth = pattern.length * width + quietZone * 2;
  canvas.width = totalWidth;
  canvas.height = height + fontSize + 4;

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000";
  let x = quietZone;
  for (const bit of pattern) {
    if (bit === "1") ctx.fillRect(x, 0, width, height);
    x += width;
  }
  ctx.font = `${fontSize}px monospace`;
  ctx.textAlign = "center";
  ctx.fillText(value, totalWidth / 2, height + fontSize);

  return canvas.toDataURL("image/png");
}

// ── Translations ──────────────────────────────────────────────
const T = {
  EN: {
    appName:"NGB Asset System", orgName:"National Gene Bank", subtitle:"Asset Management System",
    login:"Login →", loggingIn:"Verifying...", enterPassword:"Enter password",
    wrongPassword:"Incorrect password. Please try again.",
    loggedInAs:"Logged in as", logout:"Logout",
    nav:{ assets:"Assets", inventory:"Inventory", scan:"Scan Barcode", settings:"Settings" },
    addAsset:"+ Add Asset", search:"Search", searchPlaceholder:"Search assets...",
    allStatus:"All Status", allCategories:"All Categories", noAssets:"No assets found.",
    total:"Total", available:"Available", onLoan:"On Loan", maintenance:"Maintenance",
    status:{ Available:"Available", "On Loan":"On Loan", Maintenance:"Maintenance", Retired:"Retired" },
    assetDetail:"Asset Details", edit:"✏️ Edit", delete:"🗑 Delete", borrow:"📤 Borrow", close:"✕ Close",
    confirmDelete:"Delete this asset?",
    addNew:"➕ Add New Asset", editAsset:"✏️ Edit Asset",
    fields:{
      name:"Asset Name *", category:"Category", brand:"Brand / Model", serial:"Serial Number",
      qty:"Quantity", unit:"Unit", location:"Location / Room", responsible:"Responsible Person",
      source:"Source / From", purchaseDate:"Purchase Date", price:"Price",
      warranty:"Warranty Expiry", status:"Status", notes:"Notes", image:"Asset Image"
    },
    save:"💾 Save", saving:"Saving...", cancel:"Cancel",
    borrowTitle:"Borrow Asset", borrower:"Borrower Name *", dept:"Department / Room",
    contact:"Contact Number", borrowDate:"Borrow Date", dueDate:"Return Date *",
    qty2:"Quantity to Borrow", confirmBorrow:"📤 Confirm Borrow", confirmReturn:"✅ Confirm Return",
    currentBorrowers:"Current Borrowers", totalQty:"Total", onLoanQty:"On Loan", availableQty:"Available",
    borrowHistory:"Borrow History", returnedLabel:"Returned", onLoanLabel:"On Loan",
    scanTitle:"🔍 Scan Barcode", scanTitleStaff:"Search Asset by ID",
    scanDesc:"Scan barcode with Zebra DS22 or type asset ID, then press Enter",
    notFound:"❌ Asset not found.", viewFull:"View Full Details",
    printBarcode:"🖨️ Print Barcode", downloadBarcode:"⬇ Download",
    printLabel:"🖨️ Print Labels", selectForPrint:"Select for Print",
    printSelected:"🖨️ Print Selected",
    labelSize:"Label Size", labelSmall:"Small (38x25mm)", labelMedium:"Medium (57x32mm)", labelLarge:"Large (90x29mm)",
    copies:"Copies", perRow:"Per Row",
    inventoryTitle:"Inventory",
    settingsTitle:"Settings", users:"Users", categories:"Categories", locations:"Locations", sources:"Sources",
    addUser:"Add User", name:"Name", password:"Password", role:"Role",
    staffRole:"Staff (View only)", adminRole:"Admin (Full access)",
    adding:"Adding...", remove:"Remove",
    addCategory:"Add Category", icon:"Icon", laName:"LA Name",
    addLocation:"Add Location", addSource:"Add Source",
    overdue:"OVERDUE", enterName:"Please enter asset name.",
    enterBorrower:"Please enter borrower name.", enterDueDate:"Please enter return date.",
    fillAll:"Please fill in all fields.", confirmRemove:"Remove",
  },
  LA: {
    appName:"ລະບົບຊັບສິນ NGB", orgName:"ທະນາຄານເຊື້ອພັນແຫ່ງຊາດ", subtitle:"ລະບົບຈັດການຊັບສິນ",
    login:"ເຂົ້າລະບົບ →", loggingIn:"ກຳລັງກວດສອບ...", enterPassword:"ໃສ່ລະຫັດຜ່ານ",
    wrongPassword:"ລະຫັດຜ່ານບໍ່ຖືກຕ້ອງ ກະລຸນາລອງໃໝ່",
    loggedInAs:"ເຂົ້າລະບົບໃນນາມ", logout:"ອອກຈາກລະບົບ",
    nav:{ assets:"ຊັບສິນ", inventory:"ຄັງ", scan:"ສະແກນບາໂຄດ", settings:"ຕັ້ງຄ່າ" },
    addAsset:"+ ເພີ່ມຊັບສິນ", search:"ຄົ້ນຫາ", searchPlaceholder:"ຄົ້ນຫາຊັບສິນ...",
    allStatus:"ທຸກສະຖານະ", allCategories:"ທຸກໝວດໝູ່", noAssets:"ບໍ່ພົບຊັບສິນ",
    total:"ທັງໝົດ", available:"ວ່າງ", onLoan:"ຖືກຢືມ", maintenance:"ສ້ອມແປງ",
    status:{ Available:"ວ່າງ", "On Loan":"ຖືກຢືມ", Maintenance:"ສ້ອມແປງ", Retired:"ເລີກໃຊ້" },
    assetDetail:"ລາຍລະອຽດຊັບສິນ", edit:"✏️ ແກ້ໄຂ", delete:"🗑 ລຶບ", borrow:"📤 ຢືມ", close:"✕ ປິດ",
    confirmDelete:"ລຶບຊັບສິນນີ້?",
    addNew:"➕ ເພີ່ມຊັບສິນໃໝ່", editAsset:"✏️ ແກ້ໄຂຊັບສິນ",
    fields:{
      name:"ຊື່ຊັບສິນ *", category:"ໝວດໝູ່", brand:"ຍີ່ຫໍ້ / ລຸ້ນ", serial:"ໝາຍເລກຊີຣຽນ",
      qty:"ຈຳນວນ", unit:"ໜ່ວຍ", location:"ສະຖານທີ່", responsible:"ຜູ້ຮັບຜິດຊອບ",
      source:"ແຫຼ່ງທີ່ມາ", purchaseDate:"ວັນທີຊື້", price:"ລາຄາ",
      warranty:"ວັນໝົດປະກັນ", status:"ສະຖານະ", notes:"ໝາຍເຫດ", image:"ຮູບຊັບສິນ"
    },
    save:"💾 ບັນທຶກ", saving:"ກຳລັງບັນທຶກ...", cancel:"ຍົກເລີກ",
    borrowTitle:"ຢືມຊັບສິນ", borrower:"ຊື່ຜູ້ຢືມ *", dept:"ພະແນກ / ຫ້ອງ",
    contact:"ເບີຕິດຕໍ່", borrowDate:"ວັນທີຢືມ", dueDate:"ວັນກຳນົດຄືນ *",
    qty2:"ຈຳນວນທີ່ຢືມ", confirmBorrow:"📤 ຢືນຢັນການຢືມ", confirmReturn:"✅ ຢືນຢັນການຄືນ",
    currentBorrowers:"ຜູ້ຢືມຕອນນີ້", totalQty:"ທັງໝົດ", onLoanQty:"ຖືກຢືມ", availableQty:"ຄົງເຫຼືອ",
    borrowHistory:"ປະຫວັດການຢືມ", returnedLabel:"ຄືນແລ້ວ", onLoanLabel:"ກຳລັງຢືມ",
    scanTitle:"🔍 ສະແກນບາໂຄດ", scanTitleStaff:"ຄົ້ນຫາຊັບສິນດ້ວຍລະຫັດ",
    scanDesc:"ສະແກນບາໂຄດດ້ວຍ Zebra DS22 ຫຼື ພິມລະຫັດຊັບສິນ ແລ້ວກົດ Enter",
    notFound:"❌ ບໍ່ພົບຊັບສິນ", viewFull:"ເບິ່ງລາຍລະອຽດ",
    printBarcode:"🖨️ ພິມບາໂຄດ", downloadBarcode:"⬇ ດາວໂຫຼດ",
    printLabel:"🖨️ ພິມສະຕິກເກີ", selectForPrint:"ເລືອກເພື່ອພິມ",
    printSelected:"🖨️ ພິມທີ່ເລືອກ",
    labelSize:"ຂະໜາດສະຕິກເກີ", labelSmall:"ນ້ອຍ (38x25mm)", labelMedium:"ກາງ (57x32mm)", labelLarge:"ໃຫຍ່ (90x29mm)",
    copies:"ຈຳນວນສຳເນົາ", perRow:"ຕໍ່ແຖວ",
    inventoryTitle:"ຄັງຊັບສິນ",
    settingsTitle:"ຕັ້ງຄ່າ", users:"ຜູ້ໃຊ້", categories:"ໝວດໝູ່", locations:"ສະຖານທີ່", sources:"ແຫຼ່ງທີ່ມາ",
    addUser:"ເພີ່ມຜູ້ໃຊ້", name:"ຊື່", password:"ລະຫັດຜ່ານ", role:"ສິດທິ",
    staffRole:"Staff (ເບິ່ງຂໍ້ມູນໄດ້)", adminRole:"Admin (ສິດທິເຕັມ)",
    adding:"ກຳລັງເພີ່ມ...", remove:"ລຶບ",
    addCategory:"ເພີ່ມໝວດໝູ່", icon:"ໄອຄອນ", laName:"ຊື່ພາສາລາວ",
    addLocation:"ເພີ່ມສະຖານທີ່", addSource:"ເພີ່ມແຫຼ່ງທີ່ມາ",
    overdue:"ເກີນກຳນົດ", enterName:"ກະລຸນາໃສ່ຊື່ຊັບສິນ",
    enterBorrower:"ກະລຸນາໃສ່ຊື່ຜູ້ຢືມ", enterDueDate:"ກະລຸນາໃສ່ວັນກຳນົດຄືນ",
    fillAll:"ກະລຸນາຕື່ມຂໍ້ມູນໃຫ້ຄົບ", confirmRemove:"ລຶບ",
  }
};

const STATUS_COLORS = {
  Available:"#22c55e","On Loan":"#f59e0b",Maintenance:"#3b82f6",Retired:"#ef4444"
};

async function apiGet(action,params={}) {
  const q=new URLSearchParams({action,...params});
  const res=await fetch(`${API_URL}?${q}`);
  return res.json();
}
async function apiPost(action,data={}) {
  const res=await fetch(API_URL,{method:"POST",body:JSON.stringify({action,...data})});
  return res.json();
}
function genAssetId(assets) {
  const max=assets.reduce((m,a)=>{
    const n=parseInt((a["Asset ID"]||"AST-0000").split("-")[1]||"0");
    return n>m?n:m;
  },0);
  return `AST-${String(max+1).padStart(4,"0")}`;
}
function calcAvailable(asset,borrows) {
  const qty=Number(asset["Quantity"]||0);
  const borrowed=borrows.filter(b=>b["Asset ID"]===asset["Asset ID"]&&b["Borrow Status"]==="On Loan").reduce((s,b)=>s+Number(b["Quantity Borrowed"]||0),0);
  return Math.max(0,qty-borrowed);
}
function isOverdue(dueDate) {
  if(!dueDate) return false;
  const p=dueDate.toString().split("/");
  if(p.length!==3) return false;
  return new Date(p[2],p[1]-1,p[0])<new Date();
}
function getName(item,nameKey,lang) {
  if(lang==="LA"&&item["LA Name"]) return item["LA Name"];
  return item[nameKey]||"";
}

const S={
  page:{minHeight:"100vh",background:"#f1f5f9",fontFamily:"'Segoe UI',sans-serif",color:"#1e293b"},
  card:{background:"#fff",borderRadius:16,boxShadow:"0 1px 6px rgba(0,0,0,.07)",padding:20},
  input:{width:"100%",boxSizing:"border-box",border:"1.5px solid #e2e8f0",borderRadius:10,padding:"10px 14px",fontSize:14,outline:"none",background:"#f8fafc",fontFamily:"'Segoe UI',sans-serif"},
  btn:(bg="#2563eb",color="#fff",p="10px 18px")=>({background:bg,border:"none",borderRadius:10,padding:p,color,fontSize:14,cursor:"pointer",fontWeight:600,fontFamily:"'Segoe UI',sans-serif",display:"inline-flex",alignItems:"center",gap:6}),
  label:{fontSize:12,color:"#64748b",display:"block",marginBottom:4,fontWeight:600},
  badge:(s)=>({background:(STATUS_COLORS[s]||"#94a3b8")+"22",color:(STATUS_COLORS[s]||"#94a3b8"),fontSize:11,padding:"3px 10px",borderRadius:20,fontWeight:700,whiteSpace:"nowrap"}),
};

function Modal({open,onClose,children,wide}) {
  useEffect(()=>{
    const h=(e)=>e.key==="Escape"&&onClose();
    if(open) window.addEventListener("keydown",h);
    return ()=>window.removeEventListener("keydown",h);
  },[open,onClose]);
  if(!open) return null;
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",backdropFilter:"blur(4px)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:20,maxWidth:wide?720:540,width:"100%",maxHeight:"92vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.2)"}}>
        {children}
      </div>
    </div>
  );
}
function Spinner(){return <div style={{display:"flex",justifyContent:"center",padding:40}}><div style={{width:36,height:36,border:"4px solid #e2e8f0",borderTop:"4px solid #2563eb",borderRadius:"50%",animation:"spin .8s linear infinite"}}/><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;}
function LangSwitcher({lang,setLang}){return <div style={{display:"flex",gap:4}}>{[["EN","🇬🇧"],["LA","🇱🇦"]].map(([l,f])=>(<button key={l} onClick={()=>setLang(l)} style={{background:lang===l?"rgba(255,255,255,.3)":"rgba(255,255,255,.1)",border:"none",borderRadius:8,padding:"5px 10px",color:"#fff",fontSize:12,cursor:"pointer",fontWeight:lang===l?700:400}}>{f} {l}</button>))}</div>;}

// ── Print Labels Modal ────────────────────────────────────────
function PrintLabelsModal({assets,selected,onClose,t}) {
  const [labelSize,setLabelSize]=useState("medium");
  const [copies,setCopies]=useState(1);
  const [perRow,setPerRow]=useState(3);

  const sizes={
    small:{w:140,h:80,bw:1,bh:35,fs:8},
    medium:{w:200,h:110,bw:2,bh:50,fs:10},
    large:{w:300,h:100,bw:2,bh:55,fs:11},
  };

  const handlePrint=()=>{
    const sz=sizes[labelSize];
    const selectedAssets=assets.filter(a=>selected.includes(a["Asset ID"]));
    const allItems=[];
    selectedAssets.forEach(a=>{
      for(let i=0;i<copies;i++) allItems.push(a);
    });

    const rows=[];
    for(let i=0;i<allItems.length;i+=perRow){
      rows.push(allItems.slice(i,i+perRow));
    }

    const itemHtml=(a)=>{
      const bc=getBarcodeDataURL(a["Asset ID"],sz.bw,sz.bh);
      return `<div style="width:${sz.w}px;height:${sz.h}px;border:1px solid #ccc;padding:6px;text-align:center;box-sizing:border-box;display:inline-flex;flex-direction:column;align-items:center;justify-content:center;margin:4px;break-inside:avoid">
        <img src="${bc}" style="max-width:100%;height:${sz.bh+sz.fs+4}px"/>
        <div style="font-size:${sz.fs}px;font-weight:bold;margin-top:2px;word-break:break-all">${a["Asset Name"]}</div>
        <div style="font-size:${sz.fs-1}px;color:#666">NGB Asset System</div>
      </div>`;
    };

    const html=`<html><head><style>
      @media print{body{margin:0}@page{margin:10mm}}
      body{font-family:sans-serif}
      .row{display:flex;flex-wrap:wrap;justify-content:flex-start}
    </style></head><body>
      <div class="row">${allItems.map(a=>itemHtml(a)).join("")}</div>
      <script>window.onload=()=>{window.print();}<\/script>
    </body></html>`;

    const w=window.open("","_blank");
    w.document.write(html);
    w.document.close();
  };

  const selectedAssets=assets.filter(a=>selected.includes(a["Asset ID"]));

  return(
    <div style={{padding:28}}>
      <h2 style={{margin:"0 0 20px",fontSize:18}}>🖨️ {t.printLabel}</h2>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:20}}>
        <label>
          <span style={S.label}>{t.labelSize}</span>
          <select value={labelSize} onChange={e=>setLabelSize(e.target.value)} style={S.input}>
            <option value="small">{t.labelSmall}</option>
            <option value="medium">{t.labelMedium}</option>
            <option value="large">{t.labelLarge}</option>
          </select>
        </label>
        <label>
          <span style={S.label}>{t.copies}</span>
          <input type="number" min={1} max={10} value={copies} onChange={e=>setCopies(Number(e.target.value))} style={S.input}/>
        </label>
        <label>
          <span style={S.label}>{t.perRow}</span>
          <input type="number" min={1} max={6} value={perRow} onChange={e=>setPerRow(Number(e.target.value))} style={S.input}/>
        </label>
      </div>

      {/* Preview */}
      <div style={{background:"#f8fafc",borderRadius:12,padding:16,marginBottom:20}}>
        <div style={{fontSize:12,color:"#64748b",marginBottom:10,fontWeight:600}}>Preview ({selectedAssets.length} items × {copies} copies = {selectedAssets.length*copies} labels)</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,maxHeight:300,overflowY:"auto"}}>
          {selectedAssets.slice(0,6).map(a=>(
            <div key={a["Asset ID"]} style={{border:"1px solid #e2e8f0",borderRadius:8,padding:8,textAlign:"center",background:"#fff"}}>
              <BarcodeCanvas value={a["Asset ID"]} width={sizes[labelSize].bw} height={sizes[labelSize].bh} fontSize={sizes[labelSize].fs}/>
              <div style={{fontSize:10,fontWeight:700,marginTop:4,maxWidth:120,wordBreak:"break-all"}}>{a["Asset Name"]}</div>
            </div>
          ))}
          {selectedAssets.length>6&&<div style={{display:"flex",alignItems:"center",color:"#94a3b8",fontSize:13}}>+{selectedAssets.length-6} more...</div>}
        </div>
      </div>

      <div style={{display:"flex",gap:10}}>
        <button onClick={handlePrint} style={S.btn()}>🖨️ {t.printSelected} ({selectedAssets.length*copies})</button>
        <button onClick={onClose} style={S.btn("#f1f5f9","#64748b")}>{t.cancel}</button>
      </div>
    </div>
  );
}

// ── Image Upload ──────────────────────────────────────────────
function ImageUpload({value,onChange,t}) {
  const fileRef=useRef(null);
  const cameraRef=useRef(null);

  const handleFile=(e)=>{
    const file=e.target.files[0];
    if(!file) return;
    const reader=new FileReader();
    reader.onload=(ev)=>onChange(ev.target.result);
    reader.readAsDataURL(file);
  };

  return(
    <div style={{marginBottom:20,textAlign:"center"}}>
      <div style={{width:140,height:140,background:"#f1f5f9",borderRadius:12,margin:"0 auto 10px",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",fontSize:50,border:"2px dashed #e2e8f0"}}>
        {value?<img src={value} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:"📷"}
      </div>
      <span style={S.label}>{t.fields.image}</span>
      <div style={{display:"flex",gap:8,justifyContent:"center",marginTop:6}}>
        <button type="button" onClick={()=>cameraRef.current.click()} style={S.btn("#0369a1","#fff","8px 14px")}>📷 Camera</button>
        <button type="button" onClick={()=>fileRef.current.click()} style={S.btn("#6366f1","#fff","8px 14px")}>🖼️ Gallery</button>
        {value&&<button type="button" onClick={()=>onChange("")} style={S.btn("#fee2e2","#ef4444","8px 14px")}>✕</button>}
      </div>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} style={{display:"none"}}/>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{display:"none"}}/>
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────
function LoginScreen({onLogin,lang,setLang,t}) {
  const [pw,setPw]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const handleLogin=async()=>{
    if(!pw.trim()) return;
    setLoading(true); setErr("");
    const res=await apiGet("login",{password:pw});
    setLoading(false);
    if(res.success) onLogin(res); else setErr(t.wrongPassword);
  };
  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#1e3a8a,#2563eb)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{position:"absolute",top:16,right:16}}><LangSwitcher lang={lang} setLang={setLang}/></div>
      <div style={{background:"#fff",borderRadius:24,padding:"40px 36px",maxWidth:380,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.25)",textAlign:"center"}}>
        <div style={{width:72,height:72,background:"linear-gradient(135deg,#1e3a8a,#2563eb)",borderRadius:20,display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 16px"}}>🌾</div>
        <h1 style={{margin:"0 0 4px",fontSize:22,color:"#1e293b",fontWeight:800}}>{t.orgName}</h1>
        <p style={{margin:"0 0 28px",fontSize:13,color:"#64748b"}}>{t.subtitle}</p>
        <input type="password" value={pw} onChange={e=>setPw(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&handleLogin()} placeholder={t.enterPassword}
          style={{...S.input,marginBottom:12,textAlign:"center",fontSize:18,letterSpacing:6}}/>
        {err&&<div style={{color:"#ef4444",fontSize:13,marginBottom:12}}>{err}</div>}
        <button onClick={handleLogin} disabled={loading}
          style={{...S.btn(),width:"100%",justifyContent:"center",padding:"13px",fontSize:15}}>
          {loading?t.loggingIn:t.login}
        </button>
      </div>
    </div>
  );
}

// ── Asset Form ────────────────────────────────────────────────
function AssetForm({initial,assets,settings,onSave,onCancel,onDelete,t,isAdmin,lang}) {
  const blank={"Asset ID":"","Asset Name":"","Category":settings.categories[0]?.["Category Name"]||"","Brand/Model":"","Serial Number":"","Quantity":1,"Unit":"unit","Location":settings.locations[0]?.["Location Name"]||"","Responsible Person":"","Source":settings.sources[0]?.["Source Name"]||"","Purchase Date":"","Price":"","Warranty Expiry":"","Status":"Available","Image URL":"","Notes":""};
  const [f,setF]=useState(initial||blank);
  const [saving,setSaving]=useState(false);
  const set=(k,v)=>setF(p=>({...p,[k]:v}));

  const handleSave=async()=>{
    if(!f["Asset Name"].trim()) return alert(t.enterName);
    setSaving(true);
    const data={...f,"Asset ID":f["Asset ID"]||genAssetId(assets)};
    const res=await apiPost(initial?"updateAsset":"addAsset",{data});
    setSaving(false);
    if(res.success) onSave(); else alert("Error: "+(res.error||"Unknown"));
  };

  const handleCatChange=(displayName)=>{const found=settings.categories.find(c=>getName(c,"Category Name",lang)===displayName);set("Category",found?.["Category Name"]||displayName);};
  const handleLocChange=(displayName)=>{const found=settings.locations.find(l=>getName(l,"Location Name",lang)===displayName);set("Location",found?.["Location Name"]||displayName);};
  const handleSrcChange=(displayName)=>{const found=settings.sources.find(s=>getName(s,"Source Name",lang)===displayName);set("Source",found?.["Source Name"]||displayName);};
  const getDisplayCat=()=>{const found=settings.categories.find(c=>c["Category Name"]===f["Category"]);return found?getName(found,"Category Name",lang):f["Category"];};
  const getDisplayLoc=()=>{const found=settings.locations.find(l=>l["Location Name"]===f["Location"]);return found?getName(found,"Location Name",lang):f["Location"];};
  const getDisplaySrc=()=>{const found=settings.sources.find(s=>s["Source Name"]===f["Source"]);return found?getName(found,"Source Name",lang):f["Source"];};

  return(
    <div style={{padding:28}}>
      <h2 style={{margin:"0 0 20px",fontSize:18,color:"#1e293b"}}>{initial?t.editAsset:t.addNew}</h2>
      <ImageUpload value={f["Image URL"]} onChange={v=>set("Image URL",v)} t={t}/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
        {[[t.fields.name,"Asset Name","text"],[t.fields.brand,"Brand/Model","text"],[t.fields.serial,"Serial Number","text"],[t.fields.qty,"Quantity","number"],[t.fields.unit,"Unit","text"],[t.fields.responsible,"Responsible Person","text"],[t.fields.purchaseDate,"Purchase Date","date"],[t.fields.warranty,"Warranty Expiry","date"]].map(([label,k,type])=>(
          <label key={k} style={{display:"block",marginBottom:14}}>
            <span style={S.label}>{label}</span>
            <input type={type} value={f[k]} onChange={e=>set(k,e.target.value)} style={S.input}/>
          </label>
        ))}
        {isAdmin&&<label style={{display:"block",marginBottom:14}}><span style={S.label}>{t.fields.price}</span><input type="number" value={f["Price"]} onChange={e=>set("Price",e.target.value)} style={S.input}/></label>}
        <label style={{display:"block",marginBottom:14}}><span style={S.label}>{t.fields.category}</span><select value={getDisplayCat()} onChange={e=>handleCatChange(e.target.value)} style={S.input}>{settings.categories.map(c=><option key={c["Category Name"]}>{getName(c,"Category Name",lang)}</option>)}</select></label>
        <label style={{display:"block",marginBottom:14}}><span style={S.label}>{t.fields.location}</span><select value={getDisplayLoc()} onChange={e=>handleLocChange(e.target.value)} style={S.input}>{settings.locations.map(l=><option key={l["Location Name"]}>{getName(l,"Location Name",lang)}</option>)}</select></label>
        <label style={{display:"block",marginBottom:14}}><span style={S.label}>{t.fields.source}</span><select value={getDisplaySrc()} onChange={e=>handleSrcChange(e.target.value)} style={S.input}>{settings.sources.map(s=><option key={s["Source Name"]}>{getName(s,"Source Name",lang)}</option>)}</select></label>
        <label style={{display:"block",marginBottom:14}}><span style={S.label}>{t.fields.status}</span><select value={f["Status"]} onChange={e=>set("Status",e.target.value)} style={S.input}>{Object.keys(STATUS_COLORS).map(s=><option key={s} value={s}>{t.status[s]||s}</option>)}</select></label>
      </div>
      <label style={{display:"block",marginBottom:20}}><span style={S.label}>{t.fields.notes}</span><textarea value={f["Notes"]} onChange={e=>set("Notes",e.target.value)} rows={2} style={{...S.input,resize:"vertical"}}/></label>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        <button onClick={handleSave} disabled={saving} style={S.btn()}>{saving?t.saving:t.save}</button>
        <button onClick={onCancel} style={S.btn("#f1f5f9","#64748b")}>{t.cancel}</button>
        {initial&&isAdmin&&<button onClick={onDelete} style={{...S.btn("#fee2e2","#ef4444"),marginLeft:"auto"}}>{t.delete}</button>}
      </div>
    </div>
  );
}

// ── Borrow Form ───────────────────────────────────────────────
function BorrowForm({asset,borrows,onSave,onCancel,t}) {
  const avail=calcAvailable(asset,borrows);
  const [f,setF]=useState({"Borrower Name":"","Department":"","Contact":"","Quantity Borrowed":1,"Borrow Date":new Date().toISOString().slice(0,10),"Due Date":"","Notes":""});
  const [saving,setSaving]=useState(false);
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const handleSave=async()=>{
    if(!f["Borrower Name"].trim()) return alert(t.enterBorrower);
    if(!f["Due Date"]) return alert(t.enterDueDate);
    if(Number(f["Quantity Borrowed"])>avail) return alert(`Max: ${avail}`);
    setSaving(true);
    const res=await apiPost("borrowAsset",{data:{...f,"Asset ID":asset["Asset ID"],"Asset Name":asset["Asset Name"]}});
    setSaving(false);
    if(res.success) onSave(); else alert("Error: "+(res.error||""));
  };
  return(
    <div style={{padding:28}}>
      <h2 style={{margin:"0 0 8px",fontSize:18}}>📤 {t.borrowTitle}</h2>
      <div style={{background:"#f0f9ff",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:"#0369a1"}}>
        {asset["Asset ID"]} — {asset["Asset Name"]}
        <div style={{marginTop:4,display:"flex",gap:16,fontSize:12}}>
          <span>{t.totalQty}: <strong>{asset["Quantity"]}</strong></span>
          <span style={{color:"#22c55e"}}>{t.availableQty}: <strong>{avail}</strong></span>
        </div>
      </div>
      {[[t.borrower,"Borrower Name","text"],[t.dept,"Department","text"],[t.contact,"Contact","tel"],[t.borrowDate,"Borrow Date","date"],[t.dueDate,"Due Date","date"]].map(([label,k,type])=>(
        <label key={k} style={{display:"block",marginBottom:12}}><span style={S.label}>{label}</span><input type={type} value={f[k]} onChange={e=>set(k,e.target.value)} style={S.input}/></label>
      ))}
      <label style={{display:"block",marginBottom:12}}><span style={S.label}>{t.qty2} (max: {avail})</span><input type="number" min={1} max={avail} value={f["Quantity Borrowed"]} onChange={e=>set("Quantity Borrowed",e.target.value)} style={S.input}/></label>
      <label style={{display:"block",marginBottom:20}}><span style={S.label}>Notes</span><textarea value={f["Notes"]} onChange={e=>set("Notes",e.target.value)} rows={2} style={{...S.input,resize:"vertical"}}/></label>
      <div style={{display:"flex",gap:10}}>
        <button onClick={handleSave} disabled={saving} style={S.btn("#f59e0b")}>{saving?t.saving:t.confirmBorrow}</button>
        <button onClick={onCancel} style={S.btn("#f1f5f9","#64748b")}>{t.cancel}</button>
      </div>
    </div>
  );
}

// ── Asset Detail ──────────────────────────────────────────────
function AssetDetail({asset,borrows,isAdmin,onEdit,onBorrow,onReturn,onClose,t,settings,lang}) {
  const avail=calcAvailable(asset,borrows);
  const activeBorrows=borrows.filter(b=>b["Asset ID"]===asset["Asset ID"]&&b["Borrow Status"]==="On Loan");
  const history=borrows.filter(b=>b["Asset ID"]===asset["Asset ID"]);
  const st=asset["Status"]||"Available";

  const getDisplayName=(value,list,nameKey)=>{const found=list.find(i=>i[nameKey]===value);return found?getName(found,nameKey,lang):value;};

  const handlePrint=()=>{
    const bc=getBarcodeDataURL(asset["Asset ID"],2,60);
    const w=window.open("","_blank");
    w.document.write(`<html><body style="margin:20px;font-family:sans-serif;text-align:center">
      <div style="display:inline-block;border:2px solid #000;padding:16px;min-width:200px">
        <img src="${bc}" style="display:block;margin:0 auto"/>
        <div style="font-size:13px;font-weight:bold;margin-top:8px">${asset["Asset Name"]}</div>
        <div style="font-size:11px;color:#666">NGB Asset System</div>
      </div>
      <script>window.onload=()=>{window.print();window.close();}<\/script>
    </body></html>`);
  };

  return(
    <div style={{padding:28}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <div style={{fontSize:11,color:"#2563eb",fontWeight:700,letterSpacing:1,marginBottom:4}}>{asset["Asset ID"]}</div>
          <h2 style={{margin:0,fontSize:20,color:"#1e293b"}}>{asset["Asset Name"]}</h2>
        </div>
        <span style={S.badge(st)}>{t.status[st]||st}</span>
      </div>

      {asset["Image URL"]&&(
        <div style={{marginBottom:16,borderRadius:12,overflow:"hidden",height:200}}>
          <img src={asset["Image URL"]} alt="" style={{width:"100%",height:200,objectFit:"cover"}}/>
        </div>
      )}

      {/* Barcode */}
      <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
        <div style={{background:"#fff",border:"2px solid #e2e8f0",borderRadius:16,padding:16,textAlign:"center"}}>
          <BarcodeCanvas value={asset["Asset ID"]} width={2} height={70} fontSize={13}/>
        </div>
      </div>

      {/* Quantity */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:16}}>
        {[[t.totalQty,asset["Quantity"]||0,"#2563eb"],[t.onLoanQty,Number(asset["Quantity"]||0)-avail,"#f59e0b"],[t.availableQty,avail,"#22c55e"]].map(([l,v,c])=>(
          <div key={l} style={{background:"#f8fafc",borderRadius:10,padding:"10px",textAlign:"center",borderTop:`3px solid ${c}`}}>
            <div style={{fontSize:22,fontWeight:800,color:c}}>{v}</div>
            <div style={{fontSize:10,color:"#94a3b8"}}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
        {[
          [t.fields.category,getDisplayName(asset["Category"],settings.categories,"Category Name")],
          [t.fields.brand,asset["Brand/Model"]],
          [t.fields.serial,asset["Serial Number"]],
          [t.fields.location,getDisplayName(asset["Location"],settings.locations,"Location Name")],
          ...(isAdmin?[[t.fields.responsible,asset["Responsible Person"]],[t.fields.source,getDisplayName(asset["Source"],settings.sources,"Source Name")],[t.fields.price,asset["Price"]?Number(asset["Price"]).toLocaleString():"-"],[t.fields.purchaseDate,asset["Purchase Date"]],[t.fields.warranty,asset["Warranty Expiry"]]]:[ ]),
        ].map(([k,v])=>(
          <div key={k} style={{background:"#f8fafc",borderRadius:10,padding:"10px 12px"}}>
            <div style={{fontSize:10,color:"#94a3b8",fontWeight:600}}>{k}</div>
            <div style={{fontSize:13,color:"#334155",marginTop:2}}>{v||"-"}</div>
          </div>
        ))}
      </div>

      {activeBorrows.length>0&&(
        <div style={{background:"#fffbeb",border:"1.5px solid #fde68a",borderRadius:12,padding:14,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:8}}>📤 {t.currentBorrowers}</div>
          {activeBorrows.map((b,i)=>(
            <div key={i} style={{fontSize:13,color:"#78350f",marginBottom:6,paddingBottom:6,borderBottom:i<activeBorrows.length-1?"1px solid #fde68a":"none"}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <span>👤 <strong>{b["Borrower Name"]}</strong> ({b["Department"]||"-"})</span>
                <span style={{fontWeight:700,color:"#2563eb"}}>{b["Quantity Borrowed"]} {asset["Unit"]||"unit"}</span>
              </div>
              <div style={{fontSize:11,marginTop:2}}>📅 {b["Borrow Date"]} → {isOverdue(b["Due Date"])&&<span style={{color:"#ef4444"}}>⚠️ {t.overdue} </span>}Due: {b["Due Date"]}</div>
              {isAdmin&&<button onClick={()=>onReturn(b)} style={{...S.btn("#22c55e","#fff","6px 12px"),marginTop:6,fontSize:12}}>{t.confirmReturn}</button>}
            </div>
          ))}
        </div>
      )}

      {isAdmin&&history.length>0&&(
        <div style={{marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:"#64748b",marginBottom:8}}>📋 {t.borrowHistory} ({history.length})</div>
          <div style={{maxHeight:120,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
            {history.slice().reverse().map((b,i)=>(
              <div key={i} style={{background:"#f8fafc",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#475569",display:"flex",justifyContent:"space-between"}}>
                <span><strong>{b["Borrower Name"]}</strong> · {b["Quantity Borrowed"]} · {b["Borrow Date"]}</span>
                <span style={{color:b["Borrow Status"]==="Returned"?"#22c55e":"#f59e0b"}}>{b["Borrow Status"]==="Returned"?t.returnedLabel:t.onLoanLabel}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {isAdmin&&<button onClick={onEdit} style={S.btn()}>{t.edit}</button>}
        {isAdmin&&avail>0&&<button onClick={onBorrow} style={S.btn("#f59e0b")}>{t.borrow}</button>}
        <button onClick={handlePrint} style={S.btn("#0f172a")}>{t.printBarcode}</button>
        <button onClick={()=>{const a=document.createElement("a");a.href=getBarcodeDataURL(asset["Asset ID"],2,60);a.download=`${asset["Asset ID"]}-barcode.png`;a.click();}} style={S.btn("#6366f1")}>{t.downloadBarcode}</button>
        <button onClick={onClose} style={{...S.btn("#f1f5f9","#64748b"),marginLeft:"auto"}}>{t.close}</button>
      </div>
    </div>
  );
}

// ── Scan View ─────────────────────────────────────────────────
function ScanView({assets,borrows,isAdmin,onViewAsset,t,settings,lang}) {
  const [input,setInput]=useState("");
  const [result,setResult]=useState(null);
  const [notFound,setNotFound]=useState(false);
  const inputRef=useRef(null);
  useEffect(()=>{if(inputRef.current) inputRef.current.focus();},[]);

  const doScan=()=>{
    setNotFound(false); setResult(null);
    const id=input.trim().toUpperCase();
    const found=assets.find(a=>a["Asset ID"]===id||a["Serial Number"]===input.trim());
    if(found) setResult(found); else setNotFound(true);
    setInput("");
  };

  const avail=result?calcAvailable(result,borrows):0;
  const activeBorrows=result?borrows.filter(b=>b["Asset ID"]===result["Asset ID"]&&b["Borrow Status"]==="On Loan"):[];
  const getDisplayName=(value,list,nameKey)=>{const found=list.find(i=>i[nameKey]===value);return found?getName(found,nameKey,lang):value;};

  return(
    <div style={{paddingBottom:20}}>
      <div style={{...S.card,marginBottom:16}}>
        <h3 style={{margin:"0 0 8px",fontSize:16}}>{isAdmin?t.scanTitle:t.scanTitleStaff}</h3>
        <p style={{margin:"0 0 12px",fontSize:13,color:"#64748b"}}>{t.scanDesc}</p>
        <div style={{display:"flex",gap:8}}>
          <input ref={inputRef} value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&doScan()}
            placeholder="AST-0001" style={{...S.input,flex:1,fontSize:16,letterSpacing:2}}/>
          <button onClick={doScan} style={S.btn()}>{t.search}</button>
        </div>
      </div>

      {notFound&&<div style={{background:"#fee2e2",border:"1.5px solid #fca5a5",borderRadius:12,padding:16,color:"#991b1b",textAlign:"center",marginBottom:16}}>{t.notFound}</div>}

      {result&&(
        <div style={{...S.card}}>
          {result["Image URL"]&&<img src={result["Image URL"]} alt="" style={{width:"calc(100% + 40px)",height:160,objectFit:"cover",borderRadius:"12px 12px 0 0",margin:"-20px -20px 16px"}}/>}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
            <div>
              <div style={{fontSize:11,color:"#2563eb",fontWeight:700}}>{result["Asset ID"]}</div>
              <div style={{fontSize:18,fontWeight:700,color:"#1e293b"}}>{result["Asset Name"]}</div>
            </div>
            <span style={S.badge(result["Status"]||"Available")}>{t.status[result["Status"]]||result["Status"]}</span>
          </div>
          <div style={{display:"flex",justifyContent:"center",marginBottom:12}}>
            <BarcodeCanvas value={result["Asset ID"]} width={2} height={50} fontSize={11}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
            {[[t.totalQty,result["Quantity"]||0,"#2563eb"],[t.onLoanQty,Number(result["Quantity"]||0)-avail,"#f59e0b"],[t.availableQty,avail,"#22c55e"]].map(([l,v,c])=>(
              <div key={l} style={{background:"#f8fafc",borderRadius:10,padding:"8px",textAlign:"center",borderTop:`3px solid ${c}`}}>
                <div style={{fontSize:20,fontWeight:800,color:c}}>{v}</div>
                <div style={{fontSize:10,color:"#94a3b8"}}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
            {[[t.fields.location,getDisplayName(result["Location"],settings.locations,"Location Name")],[t.fields.category,getDisplayName(result["Category"],settings.categories,"Category Name")],[t.fields.brand,result["Brand/Model"]],[t.fields.serial,result["Serial Number"]]].map(([k,v])=>(
              <div key={k} style={{background:"#f8fafc",borderRadius:8,padding:"8px 12px"}}>
                <div style={{fontSize:10,color:"#94a3b8",fontWeight:600}}>{k}</div>
                <div style={{fontSize:13,color:"#334155"}}>{v||"-"}</div>
              </div>
            ))}
          </div>
          {activeBorrows.length>0&&(
            <div style={{background:"#fffbeb",border:"1.5px solid #fde68a",borderRadius:10,padding:"10px 14px",marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:4}}>📤 {t.currentBorrowers}</div>
              {activeBorrows.map((b,i)=>(
                <div key={i} style={{fontSize:13,color:"#78350f",marginBottom:4}}>
                  👤 {b["Borrower Name"]} — {b["Quantity Borrowed"]} {result["Unit"]||"unit"}
                  {isOverdue(b["Due Date"])&&<span style={{color:"#ef4444",fontWeight:700,marginLeft:8}}>⚠️ {t.overdue}</span>}
                </div>
              ))}
            </div>
          )}
          <button onClick={()=>onViewAsset(result)} style={{...S.btn(),width:"100%",justifyContent:"center"}}>{t.viewFull}</button>
        </div>
      )}

      <div style={{...S.card,marginTop:16}}>
        <div style={{fontSize:12,color:"#64748b",fontWeight:700,marginBottom:8}}>All Asset IDs</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {assets.map(a=>(
            <button key={a["Asset ID"]} onClick={()=>setInput(a["Asset ID"])}
              style={{background:"#f1f5f9",border:"1px solid #e2e8f0",borderRadius:6,padding:"4px 10px",fontSize:12,color:"#475569",cursor:"pointer",fontFamily:"monospace"}}>
              {a["Asset ID"]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Inventory View ────────────────────────────────────────────
function InventoryView({assets,borrows,settings,onViewAsset,t,lang}) {
  const [selectedCat,setSelectedCat]=useState(null);
  const catMap={};
  assets.forEach(a=>{const cat=a["Category"]||"Other";if(!catMap[cat])catMap[cat]=[];catMap[cat].push(a);});
  const getCatInfo=(name)=>settings.categories.find(c=>c["Category Name"]===name)||{Icon:"📦"};

  if(selectedCat){
    const catAssets=catMap[selectedCat]||[];
    const catInfo=getCatInfo(selectedCat);
    return(
      <div>
        <button onClick={()=>setSelectedCat(null)} style={{...S.btn("#f1f5f9","#64748b","8px 16px"),marginBottom:16}}>← Back</button>
        <h2 style={{margin:"0 0 16px",fontSize:18}}>{catInfo.Icon} {getName(catInfo,"Category Name",lang)||selectedCat} ({catAssets.length})</h2>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12}}>
          {catAssets.map(a=>{
            const avail=calcAvailable(a,borrows);
            return(
              <div key={a["Asset ID"]} onClick={()=>onViewAsset(a)}
                style={{...S.card,cursor:"pointer",padding:0,overflow:"hidden"}}
                onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(37,99,235,.15)"}
                onMouseLeave={e=>e.currentTarget.style.boxShadow=S.card.boxShadow}>
                <div style={{width:"100%",height:120,background:"#f1f5f9",display:"flex",alignItems:"center",justifyContent:"center",fontSize:40,overflow:"hidden"}}>
                  {a["Image URL"]?<img src={a["Image URL"]} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:catInfo.Icon}
                </div>
                <div style={{padding:"10px 12px"}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#1e293b",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a["Asset Name"]}</div>
                  <div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>{a["Asset ID"]}</div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6}}>
                    <span style={S.badge(a["Status"]||"Available")}>{t.status[a["Status"]]||a["Status"]}</span>
                    <span style={{fontSize:11,color:"#22c55e",fontWeight:700}}>{avail}/{a["Quantity"]||0}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return(
    <div>
      <h2 style={{margin:"0 0 16px",fontSize:18}}>🗄️ {t.inventoryTitle}</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:12}}>
        {Object.entries(catMap).map(([cat,items])=>{
          const info=getCatInfo(cat);
          return(
            <div key={cat} onClick={()=>setSelectedCat(cat)}
              style={{...S.card,cursor:"pointer",textAlign:"center",padding:"24px 16px"}}
              onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(37,99,235,.15)"}
              onMouseLeave={e=>e.currentTarget.style.boxShadow=S.card.boxShadow}>
              <div style={{fontSize:48,marginBottom:8}}>{info.Icon}</div>
              <div style={{fontSize:14,fontWeight:700,color:"#1e293b",marginBottom:4}}>{getName(info,"Category Name",lang)||cat}</div>
              <div style={{fontSize:12,color:"#94a3b8"}}>{items.length} {lang==="LA"?"ລາຍການ":"items"}</div>
            </div>
          );
        })}
        {Object.keys(catMap).length===0&&<div style={{gridColumn:"1/-1",textAlign:"center",color:"#94a3b8",padding:40}}>{t.noAssets}</div>}
      </div>
    </div>
  );
}

// ── Settings View ─────────────────────────────────────────────
function SettingsView({settings,admins,onRefresh,t,lang}) {
  const [activeTab,setActiveTab]=useState("users");
  const [form,setForm]=useState({});
  const [saving,setSaving]=useState(false);

  const handleAdd=async(action,data)=>{
    const firstVal=Object.values(data)[0];
    if(!firstVal||!String(firstVal).trim()) return alert(t.fillAll);
    setSaving(true);
    await apiPost(action,{data});
    setSaving(false);
    setForm({});
    onRefresh();
  };

  const handleDelete=async(action,name)=>{
    if(!window.confirm(`${t.confirmRemove} "${name}"?`)) return;
    await apiPost(action,{name});
    onRefresh();
  };

  const ListItem=({enName,laName,onDel})=>(
    <div style={{display:"flex",alignItems:"center",gap:10,background:"#f8fafc",borderRadius:10,padding:"10px 14px",marginBottom:8}}>
      <div style={{flex:1}}>
        <div style={{fontSize:14,color:"#1e293b"}}>{lang==="LA"&&laName?laName:enName}</div>
        {laName&&<div style={{fontSize:11,color:"#94a3b8"}}>{lang==="LA"?enName:laName}</div>}
      </div>
      <button onClick={onDel} style={S.btn("#fee2e2","#ef4444","6px 12px")}>{t.remove}</button>
    </div>
  );

  return(
    <div>
      <h2 style={{margin:"0 0 16px",fontSize:18}}>⚙️ {t.settingsTitle}</h2>
      <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap"}}>
        {[["users",t.users,"👥"],["categories",t.categories,"📦"],["locations",t.locations,"📍"],["sources",t.sources,"🏢"]].map(([key,label,icon])=>(
          <button key={key} onClick={()=>setActiveTab(key)} style={S.btn(activeTab===key?"#2563eb":"#f1f5f9",activeTab===key?"#fff":"#64748b","8px 16px")}>{icon} {label}</button>
        ))}
      </div>

      {activeTab==="users"&&(
        <div>
          {admins.map((a,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:12,background:"#f8fafc",borderRadius:10,padding:"10px 14px",marginBottom:8}}>
              <div style={{width:36,height:36,background:a.Role==="admin"?"#dbeafe":"#f0fdf4",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{a.Role==="admin"?"🔐":"👤"}</div>
              <div style={{flex:1}}><div style={{fontSize:14,fontWeight:600}}>{a.Name}</div><div style={{fontSize:11,color:"#94a3b8"}}>{a.Role==="admin"?t.adminRole:t.staffRole}</div></div>
              <button onClick={()=>handleDelete("deleteAdmin",a.Name)} style={S.btn("#fee2e2","#ef4444","6px 12px")}>{t.remove}</button>
            </div>
          ))}
          <div style={{background:"#f0f9ff",borderRadius:12,padding:16,marginTop:16}}>
            <div style={{fontSize:13,fontWeight:700,color:"#0369a1",marginBottom:12}}>➕ {t.addUser}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <label><span style={S.label}>{t.name}</span><input value={form.name||""} onChange={e=>setForm(p=>({...p,name:e.target.value}))} style={S.input}/></label>
              <label><span style={S.label}>{t.password}</span><input value={form.pw||""} onChange={e=>setForm(p=>({...p,pw:e.target.value}))} style={S.input}/></label>
            </div>
            <label style={{display:"block",marginBottom:12}}><span style={S.label}>{t.role}</span>
              <select value={form.role||"staff"} onChange={e=>setForm(p=>({...p,role:e.target.value}))} style={S.input}>
                <option value="staff">{t.staffRole}</option><option value="admin">{t.adminRole}</option>
              </select>
            </label>
            <button disabled={saving} onClick={()=>handleAdd("addAdmin",{Name:form.name||"",Password:form.pw||"",Role:form.role||"staff"})} style={{...S.btn("#0369a1"),width:"100%",justifyContent:"center"}}>{saving?t.adding:"➕ "+t.addUser}</button>
          </div>
        </div>
      )}

      {activeTab==="categories"&&(
        <div>
          {settings.categories.map((c,i)=><ListItem key={i} enName={`${c.Icon} ${c["Category Name"]}`} laName={c["LA Name"]?`${c.Icon} ${c["LA Name"]}`:""} onDel={()=>handleDelete("deleteCategory",c["Category Name"])}/>)}
          <div style={{background:"#f0f9ff",borderRadius:12,padding:16,marginTop:16}}>
            <div style={{fontSize:13,fontWeight:700,color:"#0369a1",marginBottom:12}}>➕ {t.addCategory}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <label><span style={S.label}>EN Name *</span><input value={form.catName||""} onChange={e=>setForm(p=>({...p,catName:e.target.value}))} style={S.input} placeholder="Lab Equipment"/></label>
              <label><span style={S.label}>{t.laName}</span><input value={form.catLA||""} onChange={e=>setForm(p=>({...p,catLA:e.target.value}))} style={S.input} placeholder="ຊື່ລາວ"/></label>
              <label><span style={S.label}>{t.icon}</span><input value={form.catIcon||""} onChange={e=>setForm(p=>({...p,catIcon:e.target.value}))} style={S.input} placeholder="🔬"/></label>
            </div>
            <button disabled={saving} onClick={()=>handleAdd("addCategory",{"Category Name":form.catName||"","Icon":form.catIcon||"📦","Description":"","LA Name":form.catLA||""})} style={{...S.btn("#0369a1"),width:"100%",justifyContent:"center"}}>{saving?t.adding:"➕ "+t.addCategory}</button>
          </div>
        </div>
      )}

      {activeTab==="locations"&&(
        <div>
          {settings.locations.map((l,i)=><ListItem key={i} enName={l["Location Name"]} laName={l["LA Name"]} onDel={()=>handleDelete("deleteLocation",l["Location Name"])}/>)}
          <div style={{background:"#f0f9ff",borderRadius:12,padding:16,marginTop:16}}>
            <div style={{fontSize:13,fontWeight:700,color:"#0369a1",marginBottom:12}}>➕ {t.addLocation}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <label><span style={S.label}>EN Name *</span><input value={form.locName||""} onChange={e=>setForm(p=>({...p,locName:e.target.value}))} style={S.input} placeholder="Lab Room"/></label>
              <label><span style={S.label}>{t.laName}</span><input value={form.locLA||""} onChange={e=>setForm(p=>({...p,locLA:e.target.value}))} style={S.input} placeholder="ຊື່ລາວ"/></label>
            </div>
            <button disabled={saving} onClick={()=>handleAdd("addLocation",{"Location Name":form.locName||"","Description":"","LA Name":form.locLA||""})} style={{...S.btn("#0369a1"),width:"100%",justifyContent:"center"}}>{saving?t.adding:"➕ "+t.addLocation}</button>
          </div>
        </div>
      )}

      {activeTab==="sources"&&(
        <div>
          {settings.sources.map((s,i)=><ListItem key={i} enName={s["Source Name"]} laName={s["LA Name"]} onDel={()=>handleDelete("deleteSource",s["Source Name"])}/>)}
          <div style={{background:"#f0f9ff",borderRadius:12,padding:16,marginTop:16}}>
            <div style={{fontSize:13,fontWeight:700,color:"#0369a1",marginBottom:12}}>➕ {t.addSource}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <label><span style={S.label}>EN Name *</span><input value={form.srcName||""} onChange={e=>setForm(p=>({...p,srcName:e.target.value}))} style={S.input} placeholder="UNDP Project"/></label>
              <label><span style={S.label}>{t.laName}</span><input value={form.srcLA||""} onChange={e=>setForm(p=>({...p,srcLA:e.target.value}))} style={S.input} placeholder="ຊື່ລາວ"/></label>
            </div>
            <button disabled={saving} onClick={()=>handleAdd("addSource",{"Source Name":form.srcName||"","Description":"","LA Name":form.srcLA||""})} style={{...S.btn("#0369a1"),width:"100%",justifyContent:"center"}}>{saving?t.adding:"➕ "+t.addSource}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────
export default function App() {
  const [lang,setLang]=useState("EN");
  const t=T[lang];
  const [user,setUser]=useState(null);
  const [nav,setNav]=useState("assets");
  const [assets,setAssets]=useState([]);
  const [borrows,setBorrows]=useState([]);
  const [settings,setSettings]=useState({categories:[],locations:[],sources:[]});
  const [admins,setAdmins]=useState([]);
  const [loading,setLoading]=useState(true);
  const [search,setSearch]=useState("");
  const [filterStatus,setFilterStatus]=useState("all");
  const [filterCat,setFilterCat]=useState("all");
  const [modal,setModal]=useState(null);
  const [selected,setSelected]=useState([]);
  const [printMode,setPrintMode]=useState(false);
  const isAdmin=user?.role==="admin";

  const load=useCallback(async()=>{
    setLoading(true);
    const [a,b,s,ad]=await Promise.all([apiGet("getAssets"),apiGet("getBorrows"),apiGet("getSettings"),apiGet("getAdmins")]);
    setAssets(Array.isArray(a)?a:[]);
    setBorrows(Array.isArray(b)?b:[]);
    if(s&&!s.error) setSettings(s);
    setAdmins(Array.isArray(ad)?ad:[]);
    setLoading(false);
  },[]);

  useEffect(()=>{if(user) load();},[user,load]);

  if(!user) return <LoginScreen onLogin={u=>setUser(u)} lang={lang} setLang={setLang} t={t}/>;

  const filtered=assets.filter(a=>{
    const ms=!search||[a["Asset Name"],a["Asset ID"],a["Location"],a["Category"]].some(v=>v?.toString().toLowerCase().includes(search.toLowerCase()));
    const mst=filterStatus==="all"||a["Status"]===filterStatus;
    const mc=filterCat==="all"||a["Category"]===filterCat;
    return ms&&mst&&mc;
  });

  const stats={total:assets.length,available:assets.filter(a=>a["Status"]==="Available").length,onLoan:assets.filter(a=>a["Status"]==="On Loan").length,maintain:assets.filter(a=>a["Status"]==="Maintenance").length};
  const overdueBorrows=borrows.filter(b=>b["Borrow Status"]==="On Loan"&&isOverdue(b["Due Date"]));
  const getCatInfo=(name)=>settings.categories.find(c=>c["Category Name"]===name)||{Icon:"📦"};

  const handleDelete=async(id)=>{if(!window.confirm(t.confirmDelete)) return;await apiPost("deleteAsset",{id});setModal(null);load();};
  const handleReturn=async(borrow)=>{await apiPost("returnAsset",{data:{"Borrow ID":borrow["Borrow ID"],"Asset ID":borrow["Asset ID"]}});setModal(null);load();};
  const toggleSelect=(id)=>setSelected(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);

  return(
    <div style={S.page}>
      <div style={{background:"linear-gradient(135deg,#1e3a8a,#2563eb)",padding:"14px 20px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 2px 12px rgba(37,99,235,.3)",flexWrap:"wrap",position:"sticky",top:0,zIndex:100}}>
        <div style={{width:40,height:40,background:"rgba(255,255,255,.2)",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>🌾</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:15,fontWeight:700,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.appName}</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,.7)"}}>{t.loggedInAs} <strong>{user.name}</strong> · {isAdmin?"Admin":"Staff"}</div>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
          <LangSwitcher lang={lang} setLang={setLang}/>
          {isAdmin&&!printMode&&<button onClick={()=>setModal({type:"form"})} style={{...S.btn("rgba(255,255,255,.2)","#fff","7px 12px"),fontSize:12}}>{t.addAsset}</button>}
          {nav==="assets"&&isAdmin&&(
            printMode
              ?<><button onClick={()=>selected.length>0&&setModal({type:"print"})} style={{...S.btn("#f59e0b","#fff","7px 12px"),fontSize:12}}>{t.printSelected} ({selected.length})</button>
                <button onClick={()=>{setPrintMode(false);setSelected([]);}} style={{...S.btn("rgba(255,255,255,.15)","#fff","7px 12px"),fontSize:12}}>✕</button></>
              :<button onClick={()=>setPrintMode(true)} style={{...S.btn("rgba(255,255,255,.2)","#fff","7px 12px"),fontSize:12}}>{t.printLabel}</button>
          )}
          <button onClick={()=>setUser(null)} style={{...S.btn("rgba(255,255,255,.15)","#fff","7px 12px"),fontSize:12}}>{t.logout}</button>
        </div>
      </div>

      {overdueBorrows.length>0&&(
        <div style={{background:"#fef2f2",borderLeft:"4px solid #ef4444",padding:"10px 20px",fontSize:13,color:"#991b1b",display:"flex",alignItems:"center",gap:8}}>
          ⚠️ <strong>{overdueBorrows.length}</strong> {lang==="LA"?"ລາຍການເກີນກຳນົດ":"item(s) overdue!"}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,padding:"16px 16px 0"}}>
        {[[t.total,stats.total,"#2563eb"],[t.available,stats.available,"#22c55e"],[t.onLoan,stats.onLoan,"#f59e0b"],[t.maintenance,stats.maintain,"#3b82f6"]].map(([l,v,c])=>(
          <div key={l} style={{...S.card,padding:"12px 14px",borderTop:`3px solid ${c}`}}>
            <div style={{fontSize:22,fontWeight:800,color:c}}>{v}</div>
            <div style={{fontSize:11,color:"#94a3b8",marginTop:2}}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{display:"flex",gap:4,padding:"14px 16px 0",overflowX:"auto"}}>
        {[["assets",t.nav.assets,"📋"],["inventory",t.nav.inventory,"🗄️"],["scan",t.nav.scan,"🔍"],["settings",t.nav.settings,"⚙️"]].map(([key,label,icon])=>{
          if(key==="settings"&&!isAdmin) return null;
          return(<button key={key} onClick={()=>{setNav(key);setPrintMode(false);setSelected([]);}} style={{...S.btn(nav===key?"#2563eb":"#f1f5f9",nav===key?"#fff":"#64748b","8px 16px"),whiteSpace:"nowrap"}}>{icon} {label}</button>);
        })}
        <button onClick={load} style={{...S.btn("#f1f5f9","#64748b","8px 14px"),marginLeft:"auto"}}>🔄</button>
      </div>

      <div style={{padding:16}}>
        {nav==="assets"&&(
          <>
            {printMode&&<div style={{background:"#fffbeb",border:"1.5px solid #fde68a",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:13,color:"#92400e"}}>
              ✅ {t.selectForPrint} — {selected.length} {lang==="LA"?"ລາຍການ":"selected"}
            </div>}
            <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t.searchPlaceholder} style={{...S.input,flex:1,minWidth:160}}/>
              <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{...S.input,width:"auto"}}>
                <option value="all">{t.allStatus}</option>
                {Object.keys(STATUS_COLORS).map(s=><option key={s} value={s}>{t.status[s]}</option>)}
              </select>
              <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{...S.input,width:"auto"}}>
                <option value="all">{t.allCategories}</option>
                {settings.categories.map(c=><option key={c["Category Name"]} value={c["Category Name"]}>{getName(c,"Category Name",lang)}</option>)}
              </select>
            </div>
            {loading?<Spinner/>:(
              filtered.length===0
                ?<div style={{textAlign:"center",color:"#94a3b8",padding:40}}>{t.noAssets}</div>
                :<div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {filtered.map(a=>{
                    const avail=calcAvailable(a,borrows);
                    const hasOverdue=borrows.some(b=>b["Asset ID"]===a["Asset ID"]&&b["Borrow Status"]==="On Loan"&&isOverdue(b["Due Date"]));
                    const catInfo=getCatInfo(a["Category"]);
                    const locInfo=settings.locations.find(l=>l["Location Name"]===a["Location"]);
                    const isSelected=selected.includes(a["Asset ID"]);
                    return(
                      <div key={a["Asset ID"]}
                        onClick={()=>printMode?toggleSelect(a["Asset ID"]):setModal({type:"detail",data:a})}
                        style={{...S.card,display:"flex",alignItems:"center",gap:14,cursor:"pointer",padding:"12px 16px",border:isSelected?"1.5px solid #2563eb":hasOverdue?"1.5px solid #fca5a5":"1.5px solid transparent",background:isSelected?"#eff6ff":"#fff"}}
                        onMouseEnter={e=>{if(!isSelected)e.currentTarget.style.boxShadow="0 4px 16px rgba(37,99,235,.12)";}}
                        onMouseLeave={e=>{if(!isSelected)e.currentTarget.style.boxShadow=S.card.boxShadow;}}>
                        {printMode&&<input type="checkbox" checked={isSelected} onChange={()=>toggleSelect(a["Asset ID"])} onClick={e=>e.stopPropagation()} style={{width:18,height:18,flexShrink:0}}/>}
                        <div style={{width:48,height:48,background:"#f1f5f9",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0,overflow:"hidden"}}>
                          {a["Image URL"]?<img src={a["Image URL"]} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:catInfo.Icon}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:14,fontWeight:700,color:"#1e293b",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                            {hasOverdue&&<span style={{color:"#ef4444",marginRight:4}}>⚠️</span>}
                            {a["Asset Name"]}
                          </div>
                          <div style={{fontSize:11,color:"#94a3b8",marginTop:2}}>{a["Asset ID"]} · {locInfo?getName(locInfo,"Location Name",lang):a["Location"]||"-"}</div>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                          <span style={S.badge(a["Status"]||"Available")}>{t.status[a["Status"]]||a["Status"]}</span>
                          <span style={{fontSize:11,color:avail>0?"#22c55e":"#ef4444",fontWeight:600}}>{avail}/{a["Quantity"]||0} {a["Unit"]||"unit"}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
            )}
          </>
        )}
        {nav==="inventory"&&<InventoryView assets={assets} borrows={borrows} settings={settings} onViewAsset={a=>setModal({type:"detail",data:a})} t={t} lang={lang}/>}
        {nav==="scan"&&<ScanView assets={assets} borrows={borrows} isAdmin={isAdmin} onViewAsset={a=>setModal({type:"detail",data:a})} t={t} settings={settings} lang={lang}/>}
        {nav==="settings"&&isAdmin&&<SettingsView settings={settings} admins={admins} onRefresh={load} t={t} lang={lang}/>}
      </div>

      <Modal open={modal?.type==="detail"} onClose={()=>setModal(null)} wide>
        {modal?.data&&<AssetDetail asset={modal.data} borrows={borrows} isAdmin={isAdmin} onEdit={()=>setModal({type:"form",data:modal.data})} onBorrow={()=>setModal({type:"borrow",data:modal.data})} onReturn={handleReturn} onClose={()=>setModal(null)} t={t} settings={settings} lang={lang}/>}
      </Modal>
      <Modal open={modal?.type==="form"} onClose={()=>setModal(null)}>
        {isAdmin&&<AssetForm initial={modal?.data} assets={assets} settings={settings} onSave={()=>{setModal(null);load();}} onCancel={()=>setModal(null)} onDelete={()=>handleDelete(modal.data["Asset ID"])} t={t} isAdmin={isAdmin} lang={lang}/>}
      </Modal>
      <Modal open={modal?.type==="borrow"} onClose={()=>setModal(null)}>
        {modal?.data&&isAdmin&&<BorrowForm asset={modal.data} borrows={borrows} onSave={()=>{setModal(null);load();}} onCancel={()=>setModal(null)} t={t}/>}
      </Modal>
      <Modal open={modal?.type==="print"} onClose={()=>setModal(null)} wide>
        <PrintLabelsModal assets={assets} selected={selected} onClose={()=>setModal(null)} t={t}/>
      </Modal>
    </div>
  );
}
